import useLocalState from '@phntms/use-local-state';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { HealthProfessionalList } from '../../../compositions/health-professionals/list/health-professional-list';
import { HealthProfessionalCreateModal } from '../../../compositions/health-professionals/modals/health-professional-create-modal/health-professional-create-modal';
import { HealthProfessionalModal } from '../../../compositions/health-professionals/modals/health-professional-modal/health-professional-modal';
import { useAppContext } from '../../../data/context/app-context';
import { useHealthProfessionalItems } from '../../../data/data/health-professionals/use-health-professional-items';
import { useHealthProviderItems } from '../../../data/data/health-providers/use-health-provider-items';
import { AllPracticesId } from '../../../data/data/types';
import {
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
} from '../../../data/types/api/graphql';
import { Button, ButtonIcon } from '../../../elements/button/button';
import { Content } from '../../../elements/content/content';
import { BaseInputIcon } from '../../../elements/form/input/base-input';
import { InputField } from '../../../elements/form/input/input-field';
import { InputSelect } from '../../../elements/form/input/input-select';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import {
  DataTableLocationState,
  DataTablePageSize,
} from '../../../elements/table/data-table/data-table';
import {
  Pagination,
  PaginationData,
} from '../../../elements/table/pagination/pagination';
import { Tabs } from '../../../elements/tabs/tabs';

export function AdminHealthProfessionalsPage() {
  const intl = useIntl();

  const { healthProviderId } = useAppContext();

  const [status, setStatus] = useState<HealthProfessionalStatus | 'ALL'>(
    HealthProfessionalStatus.Active
  );
  const [healthProvider, setHealthProvider] = useState<string>(
    healthProviderId ?? ''
  );

  const [
    healthProfessionalCreateModalOpen,
    setHealthProfessionalCreateModalOpen,
  ] = useState<boolean>(false);

  const [healthProfessionalModalOpen, setHealthProfessionalModalOpen] =
    useState<HealthProfessionalItemDataFragment | false>(false);

  const { data: healthProvidersData } = useHealthProviderItems();

  const location = useLocation();

  const [limit, setLimit] = useLocalState<number>(
    'dtHealthProfessionalsLimit',
    DataTablePageSize
  );

  const [paging, setPaging] = useState<PaginationData>({
    page: (location?.state as DataTableLocationState)?.page ?? 1,
    total: 0,
  });

  const [query, setQuery] = useState<string>(
    (location?.state as DataTableLocationState)?.query ?? ''
  );

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems({
    healthProviderId: healthProvider,
    statuses: [status],
    take: limit,
    skip: (paging.page - 1) * limit,
    search: query,
  });

  const healthProfessionalItems = useMemo(() => {
    const total = healthProfessionalItemsData?.totalCount ?? 0;

    if (healthProfessionalItemsData) {
      setPaging(prevState => ({
        ...prevState,
        total,
      }));
    }

    return healthProfessionalItemsData?.items ?? [];
  }, [healthProfessionalItemsData]);

  useEffect(() => {
    if (query.length > 0) {
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [query]);

  const onHandleSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleLimit = useCallback(
    (value: number) => {
      setLimit(value);

      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    [setLimit]
  );

  const onHandlePagination = useCallback((value: number) => {
    setPaging(prevState => ({
      ...prevState,
      page: value,
    }));
  }, []);

  const onHandleStatus = useCallback(
    (value: HealthProfessionalStatus | 'ALL') => {
      setStatus(value);
    },
    []
  );

  const onHandleHealthProvider = useCallback((value: string) => {
    setHealthProvider(value);
  }, []);

  const onHandleHealthProfessional = useCallback(
    (value: HealthProfessionalItemDataFragment) => {
      setHealthProfessionalModalOpen(value);
    },
    []
  );

  useEffect(() => {
    setHealthProvider(healthProviderId ?? '');
  }, [healthProviderId]);

  return (
    <>
      <Content
        toolbar={
          <>
            <Tabs<HealthProfessionalStatus | 'ALL'>
              value={status}
              items={[
                {
                  key: HealthProfessionalStatus.Active,
                  title: (
                    <CustomFormattedMessage id="admin.users.active-users" />
                  ),
                },
                {
                  key: 'ALL',
                  title: <CustomFormattedMessage id="admin.users.all-users" />,
                },
              ]}
              onChange={onHandleStatus}
            />

            <InputSelect
              name="health-provider"
              value={healthProvider}
              options={[
                ...[
                  {
                    id: AllPracticesId,
                    name: <CustomFormattedMessage id="common.all-practices" />,
                  },
                ],
                ...healthProvidersData,
              ].map(option => ({
                key: option.id ?? '',
                title: option.name ?? '',
              }))}
              onChange={onHandleHealthProvider}
            />

            <InputField
              icon={BaseInputIcon.SEARCH}
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'common.patients-search-placeholders',
              })}
              onChange={value => onHandleSearchDebounce(value)}
            />
            <Button
              icon={ButtonIcon.USER_ADD}
              onClick={() => setHealthProfessionalCreateModalOpen(true)}
            >
              <CustomFormattedMessage id="admin.button.create-user" />
            </Button>
          </>
        }
      >
        <HealthProfessionalList
          loading={healthProfessionalItemsLoading}
          items={healthProfessionalItems}
          onClick={onHandleHealthProfessional}
        />

        <Pagination
          limit={limit}
          paging={paging}
          onChangeLimit={onHandleLimit}
          onChangePage={onHandlePagination}
        />
      </Content>

      {healthProfessionalModalOpen && (
        <HealthProfessionalModal
          healthProfessionalId={healthProfessionalModalOpen.id}
          onClose={() => {
            setHealthProfessionalModalOpen(false);
          }}
        />
      )}

      {healthProfessionalCreateModalOpen && (
        <HealthProfessionalCreateModal
          onClose={() => setHealthProfessionalCreateModalOpen(false)}
        />
      )}
    </>
  );
}
